import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "../../../components/MDBox";
import MDTypography from "../../../components/MDTypography";
import MDButton from "../../../components/MDButton";
import DashboardLayout from "../../../shared/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../shared/Navbars/DashboardNavbar";
import Footer from "../../../shared/Footer";
import DataTable from "../../../shared/Tables/DataTable";
import articleService from "../../../services/articleService";
import ActionCell from "./components/ActionCell";
import MDBadge from "../../../components/MDBadge";
import MDSnackbar from "../../../components/MDSnackbar";
import { formatDate } from "../../../utils/formatDate";

function ArticleList() {
  const [articles, setArticles] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    color: "info",
    title: "",
    content: "",
  });

  const closeNotification = () => setNotification({ ...notification, open: false });

  useEffect(() => {
    fetchArticles(page, limit, search);
  }, [page, limit, search]);

  const fetchArticles = async (currentPage, currentLimit, currentSearch) => {
    setLoading(true);
    try {
      const response = await articleService.getAll({
        page: currentPage + 1,
        limit: currentLimit,
        q: currentSearch,
        searchFields: "title,excerpt",
        select: "title,section,author,status,publishedAt"
      });
      setArticles(response?.data || []);
      setTotalCount(response?.total || 0);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching articles:", error);
      setNotification({
        open: true,
        color: "error",
        title: "Error",
        content: "No se pudieron cargar los artículos. Por favor, intenta de nuevo.",
      });
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "publicado":
        return "success";
      case "revisión":
        return "warning";
      case "borrador":
        return "secondary";
      default:
        return "secondary";
    }
  };

  const columns = [
    { Header: "Título", accessor: "title", width: "30%", align: "left" },
    { Header: "Sección", accessor: "section", align: "left" },
    { Header: "Autor", accessor: "author", align: "left" },
    { Header: "Estado", accessor: "status", align: "center" },
    { Header: "Fecha de publicación", accessor: "publishedAt", align: "center" },
    { Header: "Acciones", accessor: "actions", align: "center" },
  ];

  const rows = (articles || []).filter(article => article !== null).map((article) => ({
    title: <MDTypography variant="button" fontWeight="medium">{article.title || "Sin título"}</MDTypography>,
    section: <MDTypography variant="caption">{article.section?.name || "N/A"}</MDTypography>,
    author: <MDTypography variant="caption">{article.author?.name || "Desconocido"}</MDTypography>,
    status: <MDBadge badgeContent={article.status || "borrador"} color={getStatusColor(article.status)} variant="gradient" size="sm" />,
    publishedAt: <MDTypography variant="caption">{formatDate(article.publishedAt) || "No publicada"}</MDTypography>,
    actions: (
      <MDBox display="flex" alignItems="center">
        <ActionCell id={article._id} action="details" icon="visibility" color="info" />
        <ActionCell id={article._id} action="edit" icon="edit" color="info" />
        <ActionCell id={article._id} action="delete" icon={article._id ? "delete" : ""} color="primary" onDelete={fetchArticles} setNotification={setNotification} />
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
      >
        <MDButton
          component={Link}
          to="/articles/new"
          variant="gradient"
          color="info"
        >
          Nuevo
        </MDButton>
      </MDBox>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                <MDTypography variant="h6" color="white">Artículos</MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={{ defaultValue: limit, entries: [5, 10, 15, 20, 25] }}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  isManualPagination={true}
                  pageCount={Math.ceil(totalCount / limit)}
                  onPageChange={(newPage) => setPage(newPage)}
                  onPageSizeChange={(newLimit) => setLimit(newLimit)}
                  onSearchChange={(newSearch) => {
                    setSearch(newSearch);
                    setPage(0);
                  }}
                  totalEntries={totalCount}
                  loading={loading}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <MDSnackbar
        color={notification.color}
        icon={notification.color === "error" ? "warning" : "check"}
        title={notification.title}
        content={notification.content}
        dateTime="Ahora"
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
        bgWhite
      />
      <Footer />
    </DashboardLayout>
  );
}

export default ArticleList;